import { LoggingAPICall } from "../API/loggingAPI";
import {
  EnumActionNameLevel1,
  EnumActionNameLevel2,
  EnumActionNameLevel3,
  EnumCellType,
  EnumGeneratedBy,
  EnumLoggingScreenNames,
  EnumUserTargetType,
  EnumUxTypes,
  EnumWidgets,
  ILoggingAPIBody,
} from "../types/loggingTypes";
import { CustomDimensionsClass } from "./customDimensionsClass";
import { getLang } from "helpers/themeHelpers";
import { getClickStreamSearchID } from "../../clickstream/helpers/clickStreamHelpers";
import { INeighbhorhood } from "models/cities";
import { INeighborhoodAsset } from "models/Assets/INeighborhoodAsset";
import { trackLoggingEvent } from "GTMs/libs";
import { getLastScreenName } from "./loggingActions";
import { isBrowser } from "utils/nookies/utils";
import { PREV_URL } from "contexts/ListingContextHelpers/reducer";
import {IPostData} from "models/post";
import {IJobPosterPostView, IJobSeekerPostView} from "models/jobPostView";
import {iPostListingData} from "../../../screens/PostView/_private/Api/types";

export interface IPostLoggingData {
  post_id?: number | string;
  member_id?: number | string;
  category_reporting_name?: string;
  sub_category_reporting_name?: string;
  neighborhoodName?: string;
  city_name?: string;
  services?: string[];
  neighborhood?: INeighbhorhood | INeighborhoodAsset | any;
  member_isShop?: boolean;
  view_type?: string;
}
export interface IListingData {
  category?: string;
  subcategory?: string;
  neighborhood?: INeighbhorhood | INeighborhoodAsset | any;
  city_name?: string;
  postId?: number | string;
}

export interface ISendLoggingEventProps {
  actionNameLvl1: EnumActionNameLevel1;
  actionNameLvl2: EnumActionNameLevel2;
  actionNameLvl3: EnumActionNameLevel3;
  currentScreenName: EnumLoggingScreenNames;
  previousScreenName?: EnumLoggingScreenNames;
  currentWidgetName: EnumWidgets;
  ux_type: EnumUxTypes;
  post: IPostData | IJobSeekerPostView | IJobPosterPostView | iPostListingData | IPostLoggingData | any;
  referenceId: string | number;
  referenceEntity: string;
  targetId: string | number;
  cellType: EnumCellType;
  generatedBy: EnumGeneratedBy;
  listingData?: IListingData;
  userTargetType?: string;
  sessionFunnel?: EnumLoggingScreenNames;
  httpReferrer?: string;
  member_id?: string | number;
}

export const DEFAULT_VALUE = "NA";
export const MISSING_VALUE = "##MISSING##";

export const sendLoggingEvent = async ({
  actionNameLvl1,
  actionNameLvl2,
  actionNameLvl3,
  currentWidgetName,
  ux_type,
  post = {},
  referenceId,
  referenceEntity,
  targetId,
  cellType,
  generatedBy = EnumGeneratedBy.USER,
  listingData,
  userTargetType,
  sessionFunnel,
  httpReferrer,
}: ISendLoggingEventProps) => {
  const logging = CustomDimensionsClass.getInstance();
  logging.checkCurrentScreen();
  const currentScreenName = getLastScreenName();

  const {
    browserName,
    browserVersion,
    deviceBrand,
    deviceModel,
    deviceCpuArch,
    deviceLanguage,
    deviceOS,
    deviceOSVersion,
    deviceTimeZone,
  } = logging.getDeviceDetails();
  const { connectionType, connectionLatency } = logging.getConnectionInfo();
  const { utmMedium, utmCampaign, utmSource } = logging.getUtms();
  const { userId, userAuthentication } = logging.getUserInfo();
  const { source, sessionId, trackingId, sessionLife, postCountry } =
    logging.getSessionDetails();
  // @todo fix it replace it from backend
  const getPostCity = () => {
    if (listingData?.city_name) return listingData.city_name;
    if ((post as IPostData)?.city_name) return (post as IPostData)?.city_name;
    if ((post as IJobPosterPostView)?.cityName)
      return (post as IJobPosterPostView)?.cityName;
    if ((post as IJobSeekerPostView)?.cityName)
        return (post as IJobSeekerPostView)?.cityName;
    if ((post as iPostListingData)?.listing?.city?.name_english)
        return (post as iPostListingData)?.listing?.city?.name_english;
    return (post as IPostLoggingData)?.city_name ? (post as IPostLoggingData).city_name : DEFAULT_VALUE;
  };
  const getPostNeighborhood = () => {
    if (listingData?.neighborhood) return listingData.neighborhood;
    if ((post as IPostData)?.neighborhood_name) return (post as IPostData)?.neighborhood_name;
    if ((post as IJobPosterPostView)?.neighborhoodName)
      return (post as IJobPosterPostView)?.neighborhoodName;
    if ((post as IJobSeekerPostView)?.neighborhoodName)
      return (post as IJobSeekerPostView)?.neighborhoodName;
    if ((post as iPostListingData)?.listing?.neighborhood?.name_english)
      return (post as iPostListingData)?.listing?.neighborhood?.name_english;
    return (post as IPostLoggingData)?.neighborhoodName ? (post as IPostLoggingData)?.neighborhoodName : DEFAULT_VALUE;
  };
  const getPostSubCategory = () => {
    if (listingData?.subcategory) return listingData.subcategory;
    if ((post as IPostData)?.subcategory_reporting_name) return (post as IPostData)?.subcategory_reporting_name;
    if ((post as IJobPosterPostView)?.sub_category_reporting_name)
      return (post as IJobPosterPostView)?.sub_category_reporting_name;
    if ((post as IJobSeekerPostView)?.sub_category_reporting_name)
        return (post as IJobSeekerPostView)?.sub_category_reporting_name;
    if ((post as iPostListingData)?.listing?.sub_category?.reporting_name)
        return (post as iPostListingData)?.listing?.sub_category?.reporting_name;
    return (post as IPostLoggingData)?.sub_category_reporting_name ? (post as IPostLoggingData)?.sub_category_reporting_name : DEFAULT_VALUE;
  };
  const getPostPaidFeatures = () => {
    if((post as IPostData)?.services?.length > 0) return (post as IPostData)?.services[(post as IPostData)?.services.length - 1];
    if((post as IJobPosterPostView)?.card?.services?.length > 0) return (post as IJobPosterPostView)?.card?.services[(post as IJobPosterPostView)?.card?.services.length - 1];
    if((post as IJobSeekerPostView)?.card?.services?.length > 0) return (post as IJobSeekerPostView)?.card?.services[(post as IJobSeekerPostView)?.card?.services.length - 1];
    if((post as iPostListingData)?.listing?.services?.length > 0) return (post as iPostListingData)?.listing?.services[(post as iPostListingData)?.listing?.services.length - 1];
    return (post as IPostLoggingData)?.services?.length > 0 ? (post as IPostLoggingData)?.services[(post as IPostLoggingData)?.services.length - 1] : DEFAULT_VALUE;
  };

  const getPostLocation = () => {
    if (listingData?.neighborhood?.lat && listingData?.neighborhood?.lng) {
      return `${listingData?.neighborhood?.lat},${listingData?.neighborhood?.lng}`;
    }
    if ((post as IPostData)?.neighborhood?.lat && (post as IPostData)?.neighborhood?.lng) {
      return `${(post as IPostData)?.neighborhood?.lat},${(post as IPostData)?.neighborhood?.lng}`;
    }
    if ((post as IJobPosterPostView)?.card?.location) {
      return `${(post as IJobPosterPostView)?.card?.location}`;
    }
    if ((post as IJobSeekerPostView)?.card?.location) {
      return `${(post as IJobSeekerPostView)?.card?.location}`;
    }
    if ((post as iPostListingData)?.listing?.post_map?.lat && (post as iPostListingData)?.listing?.post_map?.lng) {
      return `${(post as iPostListingData)?.listing?.post_map?.lat},${(post as iPostListingData)?.listing?.post_map?.lng}`;
    }
    return (post as IPostLoggingData)?.neighborhood?.lat && (post as IPostLoggingData)?.neighborhood?.lng
        ? `${(post as IPostLoggingData)?.neighborhood?.lat},${(post as IPostLoggingData)?.neighborhood?.lng}`
        : DEFAULT_VALUE;
  };
  const getListingCategory = () => {
    if (listingData?.category) return listingData.category;
    if ((post as IPostData)?.category_reporting_name)
      return (post as IPostData)?.category_reporting_name;
    if ((post as IJobPosterPostView)?.category_reporting_name)
        return (post as IJobPosterPostView)?.category_reporting_name;
    if ((post as IJobSeekerPostView)?.category_reporting_name)
        return (post as IJobSeekerPostView)?.category_reporting_name;
    if ((post as iPostListingData)?.listing?.category?.reporting_name)
        return (post as iPostListingData)?.listing?.category?.reporting_name;
    return (post as IPostLoggingData)?.category_reporting_name ? (post as IPostLoggingData)?.category_reporting_name : DEFAULT_VALUE;
  };
  const getListingId = () => {
    if (listingData?.postId) {
      return listingData?.postId;
    } else if ((post as IPostData)?.id) {
        return (post as IPostData)?.id;
    } else if ((post as IJobPosterPostView)?.id) {
        return (post as IJobPosterPostView)?.id;
    } else if ((post as IJobSeekerPostView)?.id) {
        return (post as IJobSeekerPostView)?.id;
    } else if ((post as iPostListingData)?.listing?.listing_id) {
        return (post as iPostListingData)?.listing?.listing_id;
    } else if ((post as IPostLoggingData)?.post_id) {
        return (post as IPostLoggingData)?.post_id;
    } else {
        return DEFAULT_VALUE;
    }
  };
  const getUserTargetType = () => {
    if (post as IPostLoggingData) {
        return (post as IPostLoggingData)?.member_isShop
            ? EnumUserTargetType.Membership
            : EnumUserTargetType.Free;
    } else if ((post as IPostData)?.member_isShop) {
        return (post as IPostData)?.member_isShop
            ? EnumUserTargetType.Membership
            : EnumUserTargetType.Free;
    } else if ((post as IJobPosterPostView)?.member_isShop) {
        return (post as IJobPosterPostView)?.member_isShop
            ? EnumUserTargetType.Membership
            : EnumUserTargetType.Free;
    } else if ((post as IJobSeekerPostView)?.member_isShop) {
        return (post as IJobSeekerPostView)?.member_isShop
            ? EnumUserTargetType.Membership
            : EnumUserTargetType.Free;
    } else if ((post as iPostListingData)?.seller?.is_shop) {
        return (post as iPostListingData)?.seller?.is_shop
            ? EnumUserTargetType.Membership
            : EnumUserTargetType.Free;
    }
    return userTargetType ? userTargetType : DEFAULT_VALUE;
  };
  const {
    L1: previousActionLvl1,
    L2: previousActionLvl2,
    L3: previousActionLvl3,
  } = logging.getPreviousActions();

  logging.setSessionFunnelSource(sessionFunnel);

  const body: ILoggingAPIBody = {
    Geo_user_country: "xx",
    Geo_user_city: "NA",
    Geo_user_location: "NA",
    Geo_app_country: postCountry,
    Geo_post_country: post ? postCountry : DEFAULT_VALUE,
    Geo_post_city: getPostCity(),
    Geo_post_neighborhood: getPostNeighborhood(),
    Geo_post_location: getPostLocation(),
    Tech_device_brand: deviceBrand,
    Tech_device_model: deviceModel,
    Tech_device_screen_resolution: logging.getScreenResolution(),
    Tech_device_os: deviceOS,
    Tech_device_os_version: deviceOSVersion,
    Tech_device_browser: browserName,
    Tech_device_browser_version: browserVersion,
    Tech_device_language: deviceLanguage,
    Tech_device_timezone: deviceTimeZone,
    Tech_device_user_agent: logging.getDeviceUserAgent(),
    Tech_device_cpu_arch: deviceCpuArch,
    Tech_http_referrer:
      isBrowser() && window[PREV_URL]
        ? decodeURI(`${window.location.origin}${window[PREV_URL]}`)
        : httpReferrer
        ? decodeURI(httpReferrer)
        : decodeURI(logging.getHttpReferrer()),
    Tech_app_version: process.env.NEXT_PUBLIC_APP_VERSION || DEFAULT_VALUE,
    Tech_app_language: getLang() || DEFAULT_VALUE,
    Tech_app_platform: source,
    Tech_app_ui_theme: "light",
    Tech_connection_type: connectionType,
    Tech_connection_latency: connectionLatency,
    User_abbucket: DEFAULT_VALUE,
    User_member_id: userId,
    User_tracking_id: trackingId,
    // @todo fix it replace it from backend
    User_type: MISSING_VALUE,
    User_authentication: userAuthentication,
    User_target_id: targetId ? targetId : (post as IPostLoggingData)?.member_id ? (post as IPostLoggingData)?.member_id : (post as IPostData)?.member_id ? (post as IPostData)?.member_id : (post as IJobPosterPostView)?.card?.member_id ? (post as IJobPosterPostView)?.card?.member_id : (post as IJobSeekerPostView)?.card?.member_id ? (post as IJobSeekerPostView)?.card?.member_id : (post as iPostListingData)?.listing?.member_id ? (post as iPostListingData)?.listing?.member_id : DEFAULT_VALUE,
    // // // @todo fix it replace it from backend
    User_target_type: getUserTargetType(),
    Session_id: sessionId,
    Session_search_id:
      getClickStreamSearchID() &&
      (getClickStreamSearchID() !== "undefined" || undefined || null)
        ? getClickStreamSearchID()
        : DEFAULT_VALUE,
    Session_source: utmSource,
    Session_medium: utmMedium,
    Session_campaign: utmCampaign,
    Session_life: sessionLife,
    Session_funnel_source: logging.getSessionFunnelSource(),
    Action_screen_name: currentScreenName ?? DEFAULT_VALUE,
    Action_screen_name_previous: getLastScreenName(currentScreenName),
    Action_widget_name: currentWidgetName ? currentWidgetName : DEFAULT_VALUE,
    Action_widget_name_previous: logging.getPreviousWidget(),
    Action_ux_type: ux_type,
    Action_name: actionNameLvl1,
    Action_name_lvl2: actionNameLvl2,
    Action_name_lvl3: actionNameLvl3,
    Action_name_previous: previousActionLvl1,
    Action_name_lvl2_previous: previousActionLvl2,
    Action_name_lvl3_previous: previousActionLvl3,
    Action_listing_id: getListingId(),
    Action_listing_status: MISSING_VALUE,
    Action_listing_category: getListingCategory(),
    Action_listing_subcategory: getPostSubCategory(),
    Action_listing_paid_features: getPostPaidFeatures(),
    Action_listing_cell_type: cellType ? cellType : DEFAULT_VALUE,
    // @todo fix it replace it from backend
    Action_listing_origin_slot_type: MISSING_VALUE,
    Action_reference_id: referenceId ? referenceId : DEFAULT_VALUE,
    Action_reference_entity: referenceEntity ? referenceEntity : DEFAULT_VALUE,
    Action_generated_by: generatedBy ? generatedBy : DEFAULT_VALUE,
  };
  logging.setPreviousWidget(currentWidgetName ? currentWidgetName : "NA");
  logging.setPreviousActions({
    L1: actionNameLvl1,
    L2: actionNameLvl2,
    L3: actionNameLvl3,
  });
  trackLoggingEvent(body);
  await LoggingAPICall(body).then(() => {});
  return;
};
